<template>
  <div class="ops-view">
    <v-toolbar
      color="primary"
      :dark="$store.getters.darkTheme"
      class="ops-toolbar"
    >
      <v-toolbar-title>Operations Center</v-toolbar-title>
      <v-spacer />
      <v-select
        v-if="platformAdmin"
        v-model="selectedRuntime"
        :items="runtimeOptions"
        label="Runtime"
        dense
        dark
        hide-details
        outlined
        class="ops-toolbar-select runtime-select mr-3"
        @change="handleRuntimeChange"
      />
      <v-select
        v-if="platformAdmin"
        v-model="selectedHost"
        :items="hostOptions"
        label="Host"
        dense
        dark
        hide-details
        outlined
        class="ops-toolbar-select host-select mr-3"
        @change="refreshAll"
      />
      <v-select
        v-model="range"
        :items="rangeOptions"
        label="Range"
        dense
        dark
        hide-details
        outlined
        class="ops-toolbar-select range-select mr-3"
        @change="refreshAll"
      />
      <v-btn text class="mr-2" to="/admin">Admin Panel</v-btn>
      <v-btn :loading="loading" color="success" @click="refreshAll"
        >Refresh</v-btn
      >
    </v-toolbar>

    <v-alert
      v-if="error"
      type="error"
      class="ma-4"
      dismissible
      @input="error = ''"
    >
      {{ error }}
    </v-alert>

    <v-container fluid>
      <v-alert
        v-if="runtimeMessage"
        dense
        text
        :type="runtimeInfo.read_only ? 'warning' : 'info'"
      >
        Viewing {{ runtimeInfo.label || selectedRuntime }} /
        {{ selectedHostLabel }}. {{ runtimeMessage }}
      </v-alert>

      <!-- Site admins get this site's Users and Economics only; the
           runtime, services, containers, logs and actions panels are
           platform routes the backend reserves for the platform admin. -->
      <v-alert v-if="!platformAdmin" dense text type="info">
        Site view: users and economics for this site only.
      </v-alert>

      <v-row v-if="platformAdmin">
        <v-col cols="12" md="6">
          <v-card class="ops-card" outlined>
            <v-card-title>Total Users</v-card-title>
            <v-card-text class="metric">{{ count("users") }}</v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="ops-card" outlined>
            <v-card-title>Services Up</v-card-title>
            <v-card-text class="metric">
              {{ servicesUpCount }}/{{ servicesTotalCount }}
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-tabs v-model="tab" class="mt-4">
        <v-tab v-if="platformAdmin">Overview</v-tab>
        <v-tab v-if="platformAdmin">Services</v-tab>
        <v-tab v-if="platformAdmin">Containers</v-tab>
        <v-tab v-if="platformAdmin">Logs</v-tab>
        <v-tab>Users</v-tab>
        <v-tab>Economics</v-tab>
        <v-tab v-if="platformAdmin">Actions</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item v-if="platformAdmin">
          <v-row class="mt-4">
            <v-col
              v-for="service in services"
              :key="'ov-' + service.key"
              cols="12"
              sm="6"
              md="3"
            >
              <v-card class="ops-card ops-overview-card" outlined>
                <v-card-text>
                  <div class="d-flex align-center justify-space-between">
                    <span class="font-weight-bold">{{ service.name }}</span>
                    <v-chip :color="statusColor(service.status)" x-small dark>
                      {{ service.status }}
                    </v-chip>
                  </div>
                  <div class="caption grey--text mt-1">
                    {{ service.latency_ms }}ms
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col v-if="!services.length" cols="12">
              <v-card class="ops-card" outlined>
                <v-card-text class="grey--text">
                  No service health data yet - hit Refresh.
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item v-if="platformAdmin">
          <v-card class="mt-4">
            <v-card-title>Service Health</v-card-title>
            <v-simple-table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Latency</th>
                  <th>URL</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="service in services" :key="service.key">
                  <td>{{ service.name }}</td>
                  <td>
                    <v-chip :color="statusColor(service.status)" small dark>
                      {{ service.status }}
                    </v-chip>
                  </td>
                  <td>{{ service.latency_ms }}ms</td>
                  <td>{{ service.url }}{{ service.path }}</td>
                  <td class="detail-cell">{{ service.detail }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-tab-item>

        <v-tab-item v-if="platformAdmin">
          <v-card class="mt-4">
            <v-card-title>Containers</v-card-title>
            <v-card-text>
              <v-alert :type="containers.enabled ? 'info' : 'warning'">
                {{ containerStatusMessage }}
                <span v-if="containers.message"> {{ containers.message }}</span>
              </v-alert>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th v-if="showContainerHost">Host</th>
                    <th>Status</th>
                    <th>Image</th>
                    <th>Started</th>
                    <th>Restarts</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="container in containerRows"
                    :key="`${container.host || selectedHost}:${container.name}`"
                  >
                    <td>{{ container.name }}</td>
                    <td v-if="showContainerHost">
                      {{ container.host || selectedHostLabel }}
                    </td>
                    <td>
                      <v-chip
                        :color="container.running ? 'success' : 'grey'"
                        small
                        dark
                      >
                        {{ container.status }}
                      </v-chip>
                    </td>
                    <td>{{ container.image || "n/a" }}</td>
                    <td>{{ formatDate(container.started_at) }}</td>
                    <td>{{ container.restart_count || 0 }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item v-if="platformAdmin">
          <v-row class="mt-4">
            <v-col cols="12" md="4">
              <v-card>
                <v-card-title>
                  Log Files
                  <v-spacer />
                  <v-text-field
                    v-model="logFilter"
                    label="Filter sources"
                    dense
                    hide-details
                    clearable
                    class="log-filter"
                    @keyup.enter="reloadSelectedLog"
                  />
                </v-card-title>
                <v-list dense class="log-source-list">
                  <v-list-item
                    v-for="file in logSourceItems"
                    :key="file.path"
                    :class="{
                      'v-list-item--active': selectedLog === file.path
                    }"
                    @click="selectLogSource(file)"
                  >
                    <v-list-item-icon v-if="file.synthetic">
                      <v-icon small>mdi-source-merge</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span
                          class="log-source-dot"
                          :class="
                            sourceColorClass(file.color_key || file.service)
                          "
                        />
                        {{ file.label || file.path }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="file.synthetic">
                        Chronological tail across service logs
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-else>
                        {{ formatBytes(file.size_bytes) }} ·
                        {{ formatDate(file.modified_at) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-col>
            <v-col cols="12" md="8">
              <v-card>
                <v-card-title>
                  {{ logTitle }}
                  <v-spacer />
                  <v-btn
                    small
                    text
                    class="mr-2"
                    :color="logFollowEnabled ? 'success' : undefined"
                    @click="toggleLogFollow"
                  >
                    <v-icon small left>{{
                      logFollowEnabled ? "mdi-pause" : "mdi-play"
                    }}</v-icon>
                    {{ logFollowEnabled ? "Pause" : "Follow" }}
                  </v-btn>
                  <v-btn
                    small
                    text
                    class="mr-2"
                    :disabled="!visibleLogText"
                    @click="copyVisibleLogs"
                  >
                    <v-icon small left>mdi-content-copy</v-icon>
                    Copy
                  </v-btn>
                  <v-btn
                    small
                    text
                    class="mr-2"
                    :disabled="!visibleLogText"
                    @click="downloadVisibleLogs"
                  >
                    <v-icon small left>mdi-download</v-icon>
                    Download
                  </v-btn>
                  <v-btn small text class="mr-2" @click="loadMergedLogs"
                    >Merged</v-btn
                  >
                  <v-btn small text @click="loadErrorLog">Errors</v-btn>
                </v-card-title>
                <v-card-text class="pb-0">
                  <v-row dense align="center" class="log-controls">
                    <v-col cols="12" md="5">
                      <v-text-field
                        v-model="logTextSearch"
                        label="Search log text"
                        dense
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        @keyup.enter="reloadSelectedLog"
                        @click:clear="reloadSelectedLog"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-select
                        v-model="logServiceFilter"
                        :items="logServiceOptions"
                        label="Service"
                        dense
                        hide-details
                        :disabled="!isMergedLog"
                        @change="loadMergedLogs"
                      />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="logSeverityFilter"
                        :items="logSeverityOptions"
                        label="Severity"
                        dense
                        hide-details
                        :disabled="!isMergedLog"
                        @change="loadMergedLogs"
                      />
                    </v-col>
                    <v-col cols="12" md="2" class="text-md-right">
                      <v-btn
                        small
                        text
                        :disabled="!visibleLogText"
                        @click="scrollLogOutputToBottom"
                      >
                        <v-icon small left>mdi-arrow-down-bold</v-icon>
                        Newest
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-text>
                  <v-alert v-if="logNotice" dense text type="info">
                    {{ logNotice }}
                  </v-alert>
                  <div
                    v-if="isMergedLog"
                    ref="logOutput"
                    class="log-output log-output-merged"
                  >
                    <div
                      v-for="(entry, index) in renderedLogEntries"
                      :key="`${entry.source}:${index}`"
                      class="log-line-row"
                    >
                      <span class="log-line-prefix">
                        <span
                          class="log-service-tag"
                          :class="
                            sourceColorClass(entry.color_key || entry.service)
                          "
                        >
                          {{ entry.service_label || "UNKNOWN" }}
                        </span>
                        <span
                          v-if="mergedLogTimestamp(entry)"
                          class="log-line-timestamp"
                        >
                          {{ mergedLogTimestamp(entry) }}
                        </span>
                      </span>
                      <span class="log-line-text">{{
                        mergedLogMessage(entry)
                      }}</span>
                    </div>
                  </div>
                  <pre v-else ref="logOutput" class="log-output">{{
                    visibleLogText
                  }}</pre>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item>
          <v-card class="mt-4">
            <v-card-title>
              Users
              <span class="caption grey--text ml-3"
                >{{ usersTotal }} total</span
              >
              <v-spacer />
              <v-text-field
                v-model="usersSearch"
                label="Search users"
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="ops-table-search"
              />
            </v-card-title>
            <v-card-text class="pb-0">
              <div class="ops-pagination-bar">
                <span class="caption grey--text">
                  Showing {{ usersPageStart }}-{{ usersPageEnd }} of
                  {{ usersTotal }}
                </span>
                <v-spacer />
                <v-select
                  :value="usersPerPage"
                  :items="[25, 50, 100, 200]"
                  label="Rows"
                  dense
                  hide-details
                  outlined
                  class="ops-page-size mr-4"
                  @change="handleUsersPerPageChange"
                />
                <v-pagination
                  v-model="usersPage"
                  :length="usersPageCount"
                  :total-visible="7"
                  @input="loadUsers"
                />
              </div>
            </v-card-text>
            <v-simple-table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Credits</th>
                  <th>Used</th>
                  <th>Modal CPU</th>
                  <th>Modal GPU</th>
                  <th>LLM True Cost</th>
                  <th>LLM Charged</th>
                  <th>LLM Margin</th>
                  <th>Created</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredUsers.length === 0">
                  <td colspan="13" class="text-center grey--text py-4">
                    No users match this search.
                  </td>
                </tr>
                <tr
                  v-for="user in filteredUsers"
                  :key="user.id"
                  class="clickable-row"
                  @click="loadUserDetail(user.id)"
                >
                  <td>{{ user.id }}</td>
                  <td>{{ user.email }}</td>
                  <td>{{ user.subscription_plan }}</td>
                  <td>{{ user.subscription_status }}</td>
                  <td>
                    {{
                      moneyish(user.credits_balance + user.purchased_credits)
                    }}
                  </td>
                  <td>{{ moneyish(user.credits_used) }}</td>
                  <td>{{ moneyish(user.modal_cpu_charged_credits) }}</td>
                  <td>{{ moneyish(user.modal_gpu_charged_credits) }}</td>
                  <td>${{ moneyish(user.llm_original_cost_usd) }}</td>
                  <td>${{ moneyish(user.llm_charged_cost_usd) }}</td>
                  <td>${{ moneyish(user.llm_margin_usd) }}</td>
                  <td>{{ formatDate(user.created_at) }}</td>
                  <td class="text-right">
                    <v-btn
                      small
                      text
                      color="secondary"
                      class="mr-2"
                      :loading="assumingIdentityUserId === user.id"
                      :disabled="assumingIdentityUserId !== null"
                      @click.stop="assumeIdentity(user)"
                    >
                      Assume Identity
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="primary"
                      @click.stop="loadUserDetail(user.id)"
                    >
                      Details
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-row class="mt-4">
            <v-col cols="12" md="3">
              <v-card>
                <v-card-title>True LLM Cost</v-card-title>
                <v-card-text class="metric"
                  >${{ moneyish(llmTotals.original_cost_usd) }}</v-card-text
                >
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card>
                <v-card-title>Charged LLM Cost</v-card-title>
                <v-card-text class="metric"
                  >${{ moneyish(llmTotals.charged_cost_usd) }}</v-card-text
                >
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card>
                <v-card-title>LLM Margin</v-card-title>
                <v-card-text class="metric"
                  >${{ moneyish(llmTotals.margin_usd) }}</v-card-text
                >
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card>
                <v-card-title>Credits Charged</v-card-title>
                <v-card-text class="metric">{{
                  moneyish(llmTotals.credits_charged)
                }}</v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-alert
                v-if="economics && !economics.llm_ledger_available"
                type="warning"
              >
                LLM ledger table is not available. Apply the external LLM usage
                migration.
              </v-alert>
              <v-alert
                v-if="
                  modalExecution &&
                  modalExecution.true_cost_status === 'unknown'
                "
                type="info"
                dense
                text
              >
                {{ modalExecution.true_cost_message }}
              </v-alert>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="economicsSearch"
                label="Search economics"
                hint="Search user IDs, job IDs, agent names, providers, delivery IDs, credit transaction IDs, categories, and descriptions"
                persistent-hint
                prepend-inner-icon="mdi-magnify"
                clearable
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-card>
                <v-card-title>Modal CPU Charged</v-card-title>
                <v-card-text class="metric">{{
                  moneyish(modalTotals.cpu.net_charged_credits)
                }}</v-card-text>
                <v-card-subtitle>credits, true cost Unknown</v-card-subtitle>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card>
                <v-card-title>Modal GPU Charged</v-card-title>
                <v-card-text class="metric">{{
                  moneyish(modalTotals.gpu.net_charged_credits)
                }}</v-card-text>
                <v-card-subtitle>credits, true cost Unknown</v-card-subtitle>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card>
                <v-card-title>Modal Unknown</v-card-title>
                <v-card-text class="metric">{{
                  moneyish(modalTotals.unknown.net_charged_credits)
                }}</v-card-text>
                <v-card-subtitle
                  >charged credits needing classification</v-card-subtitle
                >
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card>
                <v-card-title>Other Credit Ledger</v-card-title>
                <v-card-text class="metric">{{
                  moneyish(modalTotals.other_credits.net_credits)
                }}</v-card-text>
                <v-card-subtitle>net non-Modal/non-LLM credits</v-card-subtitle>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card class="mb-4">
                <v-card-title>Modal Execution Breakdown</v-card-title>
                <v-simple-table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Gross Charged</th>
                      <th>Refunded</th>
                      <th>Net Charged</th>
                      <th>Minutes</th>
                      <th>True Cost</th>
                      <th>Rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in modalCategoryRows" :key="item.category">
                      <td>{{ item.label }}</td>
                      <td>{{ moneyish(item.gross_charged_credits) }}</td>
                      <td>{{ moneyish(item.refunded_credits) }}</td>
                      <td>{{ moneyish(item.net_charged_credits) }}</td>
                      <td>{{ moneyish(item.execution_minutes) }}</td>
                      <td>{{ modalTrueCostLabel(item) }}</td>
                      <td>{{ item.transactions }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>
              <v-card class="mb-4">
                <v-card-title>Recent Modal Charges</v-card-title>
                <v-simple-table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User</th>
                      <th>Job</th>
                      <th>Category</th>
                      <th>Delta</th>
                      <th>Minutes</th>
                      <th>Source</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="filteredRecentModalCharges.length === 0">
                      <td colspan="8" class="text-center grey--text py-4">
                        No Modal execution charge rows match this range/search.
                      </td>
                    </tr>
                    <tr
                      v-for="charge in filteredRecentModalCharges"
                      :key="charge.id"
                    >
                      <td>{{ charge.id }}</td>
                      <td>{{ charge.user_id }}</td>
                      <td>{{ charge.job_id || "-" }}</td>
                      <td>{{ charge.category_label }}</td>
                      <td>{{ moneyish(charge.charged_credits_delta) }}</td>
                      <td>{{ moneyish(charge.execution_minutes) }}</td>
                      <td>{{ charge.classification_source }}</td>
                      <td>{{ formatDate(charge.created_at) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>
              <v-card class="mb-4">
                <v-card-title>LLM By User</v-card-title>
                <v-simple-table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>True Cost</th>
                      <th>Charged</th>
                      <th>Margin</th>
                      <th>Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="filteredLlmByUser.length === 0">
                      <td colspan="5" class="text-center grey--text py-4">
                        No LLM user economics match this range/search.
                      </td>
                    </tr>
                    <tr
                      v-for="item in filteredLlmByUser"
                      :key="item.user_id"
                      class="clickable-row"
                      @click="loadUserDetail(item.user_id)"
                    >
                      <td>{{ item.user_id }}</td>
                      <td>${{ moneyish(item.original_cost_usd) }}</td>
                      <td>${{ moneyish(item.charged_cost_usd) }}</td>
                      <td>${{ moneyish(item.margin_usd) }}</td>
                      <td>{{ moneyish(item.credits_charged) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>
              <v-card>
                <v-card-title>Recent External LLM Charges</v-card-title>
                <v-simple-table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User</th>
                      <th>Provider</th>
                      <th>Agent</th>
                      <th>Credit Tx</th>
                      <th>Delivery</th>
                      <th>True Cost</th>
                      <th>Charged</th>
                      <th>Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="filteredRecentLlmCharges.length === 0">
                      <td colspan="9" class="text-center grey--text py-4">
                        No external LLM charge rows match this range/search.
                      </td>
                    </tr>
                    <tr
                      v-for="charge in filteredRecentLlmCharges"
                      :key="charge.id"
                    >
                      <td>{{ charge.id }}</td>
                      <td>{{ charge.user_id }}</td>
                      <td>{{ charge.provider }}</td>
                      <td>{{ charge.agent_name }}</td>
                      <td>{{ charge.credit_transaction_id || "-" }}</td>
                      <td>
                        {{
                          charge.provider_delivery_id ||
                          charge.provider_event_id ||
                          "-"
                        }}
                      </td>
                      <td>${{ moneyish(charge.original_cost_usd) }}</td>
                      <td>${{ moneyish(charge.charged_cost_usd) }}</td>
                      <td>{{ formatDate(charge.received_at) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item v-if="platformAdmin">
          <v-card class="mt-4">
            <v-card-title>{{ actionsCardTitle }}</v-card-title>
            <v-card-text>
              <v-alert :type="actions.enabled ? 'info' : 'warning'">
                {{ actionsGateMessage }}
                <span v-if="actions.message"> {{ actions.message }}</span>
              </v-alert>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Target</th>
                    <th>Workspace</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="target in visibleActionTargets" :key="target.key">
                    <td>{{ target.label }}</td>
                    <td>{{ target.cwd }}</td>
                    <td>
                      <v-chip
                        v-for="action in target.actions"
                        :key="target.key + action"
                        class="ma-1"
                        small
                        :disabled="!actions.enabled || actionRunning"
                        @click="runAction(target.key, action)"
                      >
                        {{ action }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr v-if="!visibleActionTargets.length">
                    <td colspan="3" class="text--secondary">
                      No action targets are available for this runtime.
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-container>

    <v-dialog v-model="userDialog" max-width="1200px">
      <v-card>
        <v-card-title>
          User Economics Detail
          <v-spacer />
          <v-btn
            v-if="selectedUserDetail && selectedUserDetail.user"
            small
            color="secondary"
            class="mr-2"
            :loading="assumingIdentityUserId === selectedUserDetail.user.id"
            :disabled="assumingIdentityUserId !== null"
            @click="assumeIdentity(selectedUserDetail.user)"
          >
            Assume Identity
          </v-btn>
          <v-btn icon @click="userDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="selectedUserDetail">
          <v-row>
            <v-col cols="12" md="4">
              <strong>{{ selectedUserDetail.user.email }}</strong>
              <div>User ID: {{ selectedUserDetail.user.id }}</div>
              <div>Plan: {{ selectedUserDetail.user.subscription_plan }}</div>
              <div>
                Status: {{ selectedUserDetail.user.subscription_status }}
              </div>
            </v-col>
            <v-col cols="12" md="8">
              <v-chip class="ma-1" small
                >Training: {{ selectedUserDetail.counts.training_runs }}</v-chip
              >
              <v-chip class="ma-1" small
                >Deployments:
                {{ selectedUserDetail.counts.deployments }}</v-chip
              >
              <v-chip class="ma-1" small>
                Credit transactions:
                {{ selectedUserDetail.counts.credit_transactions }}
              </v-chip>
            </v-col>
          </v-row>
          <v-alert
            v-if="!selectedUserDetail.llm_ledger_available"
            dense
            outlined
            type="warning"
            class="mt-4"
          >
            LLM usage ledger is unavailable for this runtime, so true-cost and
            charged-cost LLM figures are incomplete.
          </v-alert>
          <v-alert dense outlined type="info" class="mt-4">
            {{ userModalExecution.true_cost_message }}
          </v-alert>
          <v-row class="mt-2">
            <v-col cols="12" md="6" lg="4">
              <v-card outlined class="pa-3 fill-height">
                <div class="text-caption text-uppercase grey--text">
                  Credit Balance
                </div>
                <div class="text-h6">
                  {{ moneyish(selectedUserDetail.user.credits_balance) }}
                </div>
                <div class="text-caption">
                  Purchased:
                  {{ moneyish(selectedUserDetail.user.purchased_credits) }} |
                  Used: {{ moneyish(selectedUserDetail.user.credits_used) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" lg="4">
              <v-card outlined class="pa-3 fill-height">
                <div class="text-caption text-uppercase grey--text">
                  LLM True Cost
                </div>
                <div class="text-h6">
                  ${{ moneyish(userLlmTotals.original_cost_usd) }}
                </div>
                <div class="text-caption">
                  Charged: ${{ moneyish(userLlmTotals.charged_cost_usd) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" lg="4">
              <v-card outlined class="pa-3 fill-height">
                <div class="text-caption text-uppercase grey--text">
                  LLM Charged
                </div>
                <div class="text-h6">
                  ${{ moneyish(userLlmTotals.charged_cost_usd) }}
                </div>
                <div class="text-caption">
                  True cost: ${{ moneyish(userLlmTotals.original_cost_usd) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" lg="4">
              <v-card outlined class="pa-3 fill-height">
                <div class="text-caption text-uppercase grey--text">
                  LLM Margin
                </div>
                <div class="text-h6">
                  ${{ moneyish(userLlmTotals.margin_usd) }}
                </div>
                <div class="text-caption">
                  Charged: ${{ moneyish(userLlmTotals.charged_cost_usd) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" lg="4">
              <v-card outlined class="pa-3 fill-height">
                <div class="text-caption text-uppercase grey--text">
                  Modal CPU Charged
                </div>
                <div class="text-h6">
                  {{ moneyish(userModalTotals.cpu.net_charged_credits) }}
                  credits
                </div>
                <div class="text-caption">True cost: Unknown</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" lg="4">
              <v-card outlined class="pa-3 fill-height">
                <div class="text-caption text-uppercase grey--text">
                  Modal GPU Charged
                </div>
                <div class="text-h6">
                  {{ moneyish(userModalTotals.gpu.net_charged_credits) }}
                  credits
                </div>
                <div class="text-caption">True cost: Unknown</div>
              </v-card>
            </v-col>
          </v-row>
          <v-divider class="my-4" />
          <v-row>
            <v-col cols="12">
              <v-card outlined class="ops-user-detail-section">
                <v-card-title class="text-subtitle-1 font-weight-medium">
                  Modal Charge Breakdown
                </v-card-title>
                <div class="ops-user-detail-section__body">
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Net credits</th>
                        <th>Gross</th>
                        <th>Refunded</th>
                        <th>Minutes</th>
                        <th>True cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="category in userModalCategoryRows"
                        :key="category.label"
                      >
                        <td>{{ category.label }}</td>
                        <td>{{ moneyish(category.net_charged_credits) }}</td>
                        <td>{{ moneyish(category.gross_charged_credits) }}</td>
                        <td>{{ moneyish(category.refunded_credits) }}</td>
                        <td>{{ moneyish(category.execution_minutes) }}</td>
                        <td>
                          {{
                            category.true_cost_known
                              ? `$${moneyish(category.true_cost_usd)}`
                              : "Unknown"
                          }}
                        </td>
                      </tr>
                      <tr v-if="userModalCategoryRows.length === 0">
                        <td colspan="6">
                          No Modal charge signals in this range.
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="ops-user-detail-section">
                <v-card-title class="text-subtitle-1 font-weight-medium">
                  Recent Modal Charges
                </v-card-title>
                <div class="ops-user-detail-section__body">
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Job</th>
                        <th>Credits</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="charge in userRecentModalCharges"
                        :key="charge.id"
                      >
                        <td>{{ charge.category_label }}</td>
                        <td>{{ charge.job_id || "-" }}</td>
                        <td>{{ moneyish(charge.charged_credits_delta) }}</td>
                        <td>{{ charge.classification_source }}</td>
                      </tr>
                      <tr v-if="userRecentModalCharges.length === 0">
                        <td colspan="4">
                          No recent Modal charges in this range.
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="ops-user-detail-section">
                <v-card-title class="text-subtitle-1 font-weight-medium">
                  Recent Credit Transactions
                </v-card-title>
                <div class="ops-user-detail-section__body">
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="tx in selectedUserDetail.recent_credit_transactions"
                        :key="tx.id"
                      >
                        <td>{{ tx.transaction_type }}</td>
                        <td>{{ moneyish(tx.amount) }}</td>
                        <td>{{ tx.description }}</td>
                      </tr>
                      <tr
                        v-if="
                          selectedUserDetail.recent_credit_transactions
                            .length === 0
                        "
                      >
                        <td colspan="3">
                          No credit transactions in this range.
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="ops-user-detail-section">
                <v-card-title class="text-subtitle-1 font-weight-medium">
                  Recent LLM Charges
                </v-card-title>
                <div class="ops-user-detail-section__body">
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>True cost</th>
                        <th>Charged</th>
                        <th>Credits</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="charge in selectedUserDetail.llm.recent"
                        :key="charge.id"
                      >
                        <td>{{ charge.agent_name }}</td>
                        <td>${{ moneyish(charge.original_cost_usd) }}</td>
                        <td>${{ moneyish(charge.charged_cost_usd) }}</td>
                        <td>{{ moneyish(charge.credits_charged) }}</td>
                      </tr>
                      <tr v-if="selectedUserDetail.llm.recent.length === 0">
                        <td colspan="4">No LLM charges in this range.</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="ops-user-detail-section">
                <v-card-title class="text-subtitle-1 font-weight-medium">
                  Recent Training Runs
                </v-card-title>
                <div class="ops-user-detail-section__body">
                  <v-simple-table dense>
                    <tbody>
                      <tr
                        v-for="run in selectedUserDetail.recent_training_runs"
                        :key="run.id"
                      >
                        <td>{{ run.id }}</td>
                        <td>{{ run.status }}</td>
                        <td>{{ run.name }}</td>
                      </tr>
                      <tr
                        v-if="
                          selectedUserDetail.recent_training_runs.length === 0
                        "
                      >
                        <td colspan="3">No training runs in this range.</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { apiClient } from "@shared/services/api";

export default {
  name: "AdminOpsView",
  data() {
    return {
      tab: 0,
      loading: false,
      error: "",
      range: "30d",
      selectedRuntime: "local",
      selectedHost: "local",
      runtimeInfo: {},
      runtimeOptions: [{ text: "Local runtime", value: "local" }],
      hostOptions: [{ text: "Local host", value: "local" }],
      rangeOptions: [
        { text: "24 hours", value: "24h" },
        { text: "7 days", value: "7d" },
        { text: "30 days", value: "30d" },
        { text: "All time", value: "all" }
      ],
      overview: {},
      services: [],
      containers: {},
      logs: {},
      selectedLog: "",
      selectedLogKind: "merged",
      logLines: [],
      logEntries: [],
      logFilter: "",
      logTextSearch: "",
      logServiceFilter: "all",
      logSeverityFilter: "all",
      logSeverityOptions: [
        { text: "All", value: "all" },
        { text: "INFO", value: "INFO" },
        { text: "WARNING", value: "WARNING" },
        { text: "ERROR", value: "ERROR" },
        { text: "TRACEBACK", value: "TRACEBACK" }
      ],
      logFollowEnabled: false,
      logRefreshTimer: null,
      logFollowRefreshing: false,
      logNotice: "",
      users: [],
      usersSearch: "",
      usersPage: 1,
      usersPerPage: 100,
      usersTotal: 0,
      usersSearchTimer: null,
      economics: {},
      economicsSearch: "",
      actions: {},
      actionRunning: false,
      userDialog: false,
      selectedUserDetail: null,
      assumingIdentityUserId: null
    };
  },
  computed: {
    platformAdmin() {
      return !!this.$store.getters.isPlatformAdmin;
    },
    serviceStatusCounts() {
      return this.overview.service_status_counts || {};
    },
    servicesUpCount() {
      if (this.services.length) {
        return this.services.filter(service => service.status === "up").length;
      }
      return Number(this.serviceStatusCounts.up || 0);
    },
    servicesTotalCount() {
      if (this.services.length) {
        return this.services.length;
      }
      return Object.values(this.serviceStatusCounts).reduce(
        (sum, value) => sum + Number(value || 0),
        0
      );
    },
    logFiles() {
      return this.logs.files || [];
    },
    logSourceItems() {
      const needle = String(this.logFilter || "")
        .trim()
        .toLowerCase();
      const synthetic = {
        path: "merged",
        label: "Merged Live Logs",
        synthetic: true,
        service: "merged",
        color_key: "merged"
      };
      const files = this.logFiles
        .filter(file => {
          if (!needle) return true;
          return String(file.path || "")
            .toLowerCase()
            .includes(needle);
        })
        .map(file => ({
          ...file,
          service: this.serviceKeyForSource(file.path),
          color_key: this.serviceKeyForSource(file.path)
        }));
      return [synthetic, ...files];
    },
    logServiceOptions() {
      const services = new Map();
      (this.logFiles || []).forEach(file => {
        const service = this.serviceKeyForSource(file.path);
        if (!services.has(service)) {
          services.set(service, this.serviceLabelForKey(service));
        }
      });
      (this.logEntries || []).forEach(entry => {
        if (entry.service && !services.has(entry.service)) {
          services.set(
            entry.service,
            entry.service_label || this.serviceLabelForKey(entry.service)
          );
        }
      });
      return [
        { text: "All services", value: "all" },
        ...Array.from(services.entries())
          .sort((left, right) => left[1].localeCompare(right[1]))
          .map(([value, text]) => ({ text, value }))
      ];
    },
    isMergedLog() {
      return this.selectedLogKind === "merged";
    },
    logTitle() {
      if (this.isMergedLog) return "Merged Live Logs";
      if (this.selectedLogKind === "errors")
        return this.selectedLog || "Central Error Log";
      return this.selectedLog || "Log Output";
    },
    renderedLogEntries() {
      if (!this.logEntries.length && this.logLines.length) {
        return this.logLines.map(line => ({
          source: "merged",
          service: "unknown",
          service_label: "UNKNOWN",
          color_key: "unknown",
          line
        }));
      }
      return this.logEntries;
    },
    visibleLogText() {
      if (this.isMergedLog && this.renderedLogEntries.length) {
        return this.renderedLogEntries
          .map(
            entry =>
              entry.formatted_line ||
              `[${entry.service_label || "UNKNOWN"}] ${entry.line || ""}`
          )
          .join("\n");
      }
      return (this.logLines || []).join("\n");
    },
    llmTotals() {
      return (
        (this.economics.external_llm && this.economics.external_llm.totals) || {
          original_cost_usd: 0,
          charged_cost_usd: 0,
          margin_usd: 0,
          credits_charged: 0
        }
      );
    },
    recentLlmCharges() {
      return (
        (this.economics.external_llm && this.economics.external_llm.recent) ||
        []
      );
    },
    filteredRecentLlmCharges() {
      return this.filterRows(this.recentLlmCharges, this.economicsSearch, [
        "id",
        "user_id",
        "provider",
        "agent_id",
        "agent_name",
        "credit_transaction_id",
        "provider_delivery_id",
        "provider_event_id",
        "received_at"
      ]);
    },
    modalExecution() {
      return (
        this.economics.modal_execution || {
          true_cost_status: "unknown",
          true_cost_message: "Modal provider true cost is not available.",
          totals: {},
          recent: [],
          by_user: []
        }
      );
    },
    modalTotals() {
      return this.buildModalTotals(this.modalExecution);
    },
    modalCategoryRows() {
      return [
        this.modalTotals.cpu,
        this.modalTotals.gpu,
        this.modalTotals.unknown
      ];
    },
    recentModalCharges() {
      return this.modalExecution.recent || [];
    },
    filteredRecentModalCharges() {
      return this.filterRows(this.recentModalCharges, this.economicsSearch, [
        "id",
        "user_id",
        "job_id",
        "category",
        "category_label",
        "transaction_type",
        "classification_source",
        "description",
        "created_at"
      ]);
    },
    userLlmTotals() {
      const llm =
        (this.selectedUserDetail && this.selectedUserDetail.llm) || {};
      return (
        llm.totals || {
          original_cost_usd: 0,
          charged_cost_usd: 0,
          margin_usd: 0,
          credits_charged: 0
        }
      );
    },
    userModalExecution() {
      return (
        (this.selectedUserDetail &&
          this.selectedUserDetail.modal_execution) || {
          true_cost_status: "unknown",
          true_cost_message: "Modal provider true cost is not available.",
          totals: {},
          recent: [],
          by_user: []
        }
      );
    },
    userModalTotals() {
      return this.buildModalTotals(this.userModalExecution);
    },
    userModalCategoryRows() {
      return [
        this.userModalTotals.cpu,
        this.userModalTotals.gpu,
        this.userModalTotals.unknown
      ];
    },
    userRecentModalCharges() {
      return this.userModalExecution.recent || [];
    },
    containerRows() {
      return this.containers.containers || [];
    },
    showContainerHost() {
      return (
        this.selectedRuntime === "production_readonly" ||
        this.containerRows.some(container => container.host)
      );
    },
    containerStatusMessage() {
      if (this.selectedRuntime === "production_readonly") {
        if (this.containers.enabled) {
          return "Production container inventory is read-only.";
        }
        return "Production container inventory is unavailable.";
      }
      return `Container inspection is ${
        this.containers.enabled ? "enabled" : "disabled"
      }. Enable it with OPS_ENABLE_CONTAINER_INSPECTION=true on trusted admin hosts only.`;
    },
    filteredUsers() {
      return this.users;
    },
    usersPageCount() {
      return Math.max(
        1,
        Math.ceil((this.usersTotal || 0) / (this.usersPerPage || 1))
      );
    },
    usersPageStart() {
      if (!this.usersTotal) return 0;
      return (this.usersPage - 1) * this.usersPerPage + 1;
    },
    usersPageEnd() {
      if (!this.usersTotal) return 0;
      return Math.min(this.usersTotal, this.usersPage * this.usersPerPage);
    },
    llmByUser() {
      return (
        (this.economics.external_llm && this.economics.external_llm.by_user) ||
        []
      );
    },
    filteredLlmByUser() {
      return this.filterRows(this.llmByUser, this.economicsSearch, ["user_id"]);
    },
    visibleActionTargets() {
      if (this.selectedRuntime === "production_readonly") {
        return [];
      }
      return this.actions.targets || [];
    },
    actionsCardTitle() {
      return this.selectedRuntime === "production_readonly"
        ? "Production Read-only Actions"
        : "Allowlisted Local Actions";
    },
    actionsGateMessage() {
      if (this.selectedRuntime === "production_readonly") {
        return "Production controls are disabled.";
      }
      return `Local service control is ${
        this.actions.enabled ? "enabled" : "disabled"
      }. Enable it with OPS_ENABLE_LOCAL_SERVICE_CONTROL=true on trusted local/admin hosts only.`;
    },
    selectedHostLabel() {
      const item = this.hostOptions.find(
        option => option.value === this.selectedHost
      );
      return (item && item.text) || this.selectedHost;
    },
    runtimeMessage() {
      if (!this.runtimeInfo || !this.runtimeInfo.runtime) return "";
      const reasons = this.runtimeInfo.disabled_reasons || {};
      if (this.runtimeInfo.read_only) {
        return (
          reasons.production_adapter ||
          "This runtime is read-only. Control actions remain disabled."
        );
      }
      return this.runtimeInfo.description || "";
    }
  },
  created() {
    this.refreshAll();
  },
  beforeRouteLeave(to, from, next) {
    this.stopLogFollowTimer();
    next();
  },
  beforeDestroy() {
    this.stopLogFollowTimer();
    this.clearSearchTimers();
  },
  watch: {
    tab(value) {
      if (value === 3 && this.logFollowEnabled) {
        this.startLogFollowTimer();
        this.scrollLogOutputToBottom();
        return;
      }
      if (value === 3) {
        this.scrollLogOutputToBottom();
      }
      this.stopLogFollowTimer();
    },
    usersSearch() {
      this.scheduleUsersSearch();
    }
  },
  methods: {
    clearSearchTimers() {
      if (this.usersSearchTimer) {
        clearTimeout(this.usersSearchTimer);
        this.usersSearchTimer = null;
      }
    },
    scheduleUsersSearch() {
      if (this.usersSearchTimer) clearTimeout(this.usersSearchTimer);
      this.usersSearchTimer = setTimeout(() => {
        this.usersPage = 1;
        this.loadUsers();
      }, 250);
    },
    buildModalTotals(modalExecution) {
      const totals = (modalExecution && modalExecution.totals) || {};
      const emptyCategory = {
        gross_charged_credits: 0,
        refunded_credits: 0,
        net_charged_credits: 0,
        execution_minutes: 0,
        transactions: 0,
        true_cost_known: false,
        true_cost_usd: null
      };
      return {
        cpu: { ...emptyCategory, ...(totals.cpu || {}), label: "Modal CPU" },
        gpu: { ...emptyCategory, ...(totals.gpu || {}), label: "Modal GPU" },
        unknown: {
          ...emptyCategory,
          ...(totals.unknown || {}),
          label: "Modal Unknown"
        },
        other_credits: {
          net_credits: 0,
          transactions: 0,
          ...(totals.other_credits || {})
        }
      };
    },
    serviceKeyForSource(path) {
      const aliases = {
        admin: "admin_service",
        backtest_execution: "backtest_execution_service",
        deployment_infrastructure: "deployment_infrastructure_service",
        lean_aws: "lean_aws_service",
        market_data: "market_data_service",
        strategy_library: "strategy_library_service",
        subscription_billing: "subscription_billing_service",
        user: "user_service"
      };
      const value = String(path || "").toLowerCase();
      if (value.startsWith("services/")) {
        const service = value.split("/")[1] || "unknown";
        return aliases[service] || service;
      }
      if (value.startsWith("api/") || value.includes("main_api"))
        return "main_api";
      if (value.includes("copilot")) return "copilotclone";
      if (value.includes("billing")) return "subscription_billing_service";
      if (value.includes("market")) return "market_data_service";
      if (value.includes("backtest_execution") || value.includes("bes")) {
        return "backtest_execution_service";
      }
      if (value.includes("user")) return "user_service";
      if (value.includes("strategy")) return "strategy_library_service";
      return "unknown";
    },
    serviceLabelForKey(key) {
      const labels = {
        main_api: "MAIN_API",
        admin_service: "ADMIN",
        backtest_execution_service: "BES",
        deployment_infrastructure_service: "DEPLOY",
        lean_aws_service: "LEAN",
        market_data_service: "MARKET",
        subscription_billing_service: "BILLING",
        user_service: "USER",
        strategy_library_service: "STRATEGY",
        copilotclone: "COPILOT",
        unknown: "UNKNOWN"
      };
      return labels[key] || String(key || "unknown").replace(/_/g, " ");
    },
    sourceColorClass(key) {
      const normalized = String(key || "unknown").replace(/[^a-z0-9_-]/gi, "_");
      return `log-source-${normalized}`;
    },
    filterRows(rows, query, fields) {
      const needle = String(query || "")
        .trim()
        .toLowerCase();
      if (!needle) return rows || [];
      return (rows || []).filter(row =>
        fields.some(field =>
          String(row && row[field] != null ? row[field] : "")
            .toLowerCase()
            .includes(needle)
        )
      );
    },
    opsParams(extra = {}) {
      return {
        runtime: this.selectedRuntime,
        host: this.selectedHost,
        ...extra
      };
    },
    logContentParams(extra = {}) {
      return this.opsParams({
        ...extra,
        filter: this.logTextSearch || undefined
      });
    },
    mergedLogParams(extra = {}) {
      return this.logContentParams({
        ...extra,
        service:
          this.logServiceFilter && this.logServiceFilter !== "all"
            ? this.logServiceFilter
            : undefined,
        severity:
          this.logSeverityFilter && this.logSeverityFilter !== "all"
            ? this.logSeverityFilter
            : undefined
      });
    },
    applyRuntimeInfo(info) {
      if (!info || !info.runtime) return;
      this.runtimeInfo = info;
      this.selectedRuntime = info.runtime;
      this.runtimeOptions = (info.available_runtimes || []).map(item => ({
        text: item.label,
        value: item.value
      }));
      this.hostOptions = (info.available_hosts || []).map(item => ({
        text: item.label,
        value: item.value
      }));
      this.selectedHost = info.host;
    },
    handleRuntimeChange() {
      this.selectedHost =
        this.selectedRuntime === "production_readonly" ? "all" : "local";
      this.stopLogFollowTimer();
      this.refreshAll();
    },
    usersParams() {
      return this.opsParams({
        range: this.range,
        page: this.usersPage,
        per_page: this.usersPerPage,
        search: this.usersSearch || undefined
      });
    },
    async loadUsers() {
      const response = await apiClient.get("/admin/ops/users", {
        params: this.usersParams()
      });
      this.users = (response.data && response.data.users) || [];
      this.usersTotal = Number((response.data && response.data.total) || 0);
    },
    handleUsersPerPageChange(value) {
      this.usersPerPage = Number(value || 100);
      this.usersPage = 1;
      this.loadUsers();
    },
    async refreshAll() {
      this.loading = true;
      this.error = "";
      try {
        const rangeParams = this.opsParams({ range: this.range });
        if (!this.platformAdmin) {
          const economics = await apiClient.get("/admin/ops/economics", {
            params: rangeParams
          });
          this.economics = economics.data || {};
          await this.loadUsers();
          return;
        }
        const runtimeParams = this.opsParams();
        const responses = await Promise.all([
          apiClient.get("/admin/ops/overview", { params: rangeParams }),
          apiClient.get("/admin/ops/services", { params: runtimeParams }),
          apiClient.get("/admin/ops/containers", { params: runtimeParams }),
          apiClient.get("/admin/ops/logs", { params: runtimeParams }),
          apiClient.get("/admin/ops/logs/merged", {
            params: this.mergedLogParams()
          }),
          apiClient.get("/admin/ops/economics", { params: rangeParams }),
          apiClient.get("/admin/ops/actions", { params: runtimeParams })
        ]);
        this.overview = responses[0].data || {};
        this.applyRuntimeInfo(this.overview.runtime);
        this.services = (responses[1].data && responses[1].data.services) || [];
        this.containers = responses[2].data || {};
        this.logs = responses[3].data || {};
        this.applyMergedLogResponse(responses[4].data || {});
        this.economics = responses[5].data || {};
        this.actions = responses[6].data || {};
        await this.loadUsers();
      } catch (error) {
        console.error("Failed to load ops dashboard", error);
        this.error =
          (error.response &&
            error.response.data &&
            error.response.data.detail) ||
          "Failed to load operations data";
      } finally {
        this.loading = false;
      }
    },
    applyMergedLogResponse(data) {
      this.selectedLog = "merged";
      this.selectedLogKind = "merged";
      this.logNotice = data.message || "";
      this.logEntries = data.entries || [];
      this.logLines = data.lines || [];
      if (!this.logEntries.length && !this.logLines.length) {
        this.logLines = ["No matching merged log lines found."];
      }
      this.scrollLogOutputToBottom();
    },
    async selectLogSource(file) {
      if (file && file.synthetic) {
        await this.loadMergedLogs();
        return;
      }
      await this.selectLog(file.path);
    },
    async loadMergedLogs() {
      this.logNotice = "";
      try {
        const response = await apiClient.get("/admin/ops/logs/merged", {
          params: this.mergedLogParams({ lines: 300 })
        });
        this.applyMergedLogResponse(response.data || {});
        this.error = "";
      } catch (error) {
        this.error = "Failed to load merged logs";
      }
    },
    async selectLog(path) {
      this.selectedLog = path;
      this.selectedLogKind = "file";
      this.logEntries = [];
      this.logNotice = "";
      try {
        const response = await apiClient.get("/admin/ops/logs/tail", {
          params: this.logContentParams({
            path: path,
            lines: 300
          })
        });
        this.logLines = (response.data && response.data.lines) || [];
        if (!this.logLines.length) {
          this.logLines = [`No matching log lines found in ${path}.`];
        }
        this.error = "";
        this.scrollLogOutputToBottom();
      } catch (error) {
        this.error = "Failed to load selected log";
      }
    },
    async loadErrorLog() {
      this.selectedLogKind = "errors";
      this.logEntries = [];
      this.logNotice = "";
      try {
        const response = await apiClient.get("/admin/ops/logs/errors", {
          params: this.logContentParams({ lines: 300 })
        });
        this.selectedLog = (response.data && response.data.path) || "";
        this.logLines = (response.data && response.data.lines) || [];
        if (!this.logLines.length) {
          this.logLines = ["No matching central error log lines found."];
        }
        this.error = "";
        this.scrollLogOutputToBottom();
      } catch (error) {
        this.error = "Failed to load error log";
      }
    },
    async reloadSelectedLog() {
      if (this.selectedLogKind === "merged") {
        await this.loadMergedLogs();
        return;
      }
      if (this.selectedLogKind === "file" && this.selectedLog) {
        await this.selectLog(this.selectedLog);
        return;
      }
      await this.loadErrorLog();
    },
    toggleLogFollow() {
      this.logNotice = "";
      this.logFollowEnabled = !this.logFollowEnabled;
      if (this.logFollowEnabled && this.tab === 3) {
        this.startLogFollowTimer();
        this.refreshFollowedLog();
        return;
      }
      this.stopLogFollowTimer();
    },
    startLogFollowTimer() {
      if (this.logRefreshTimer) return;
      this.logRefreshTimer = window.setInterval(() => {
        this.refreshFollowedLog();
      }, 5000);
    },
    stopLogFollowTimer() {
      if (!this.logRefreshTimer) return;
      window.clearInterval(this.logRefreshTimer);
      this.logRefreshTimer = null;
    },
    async refreshFollowedLog() {
      if (
        !this.logFollowEnabled ||
        this.tab !== 3 ||
        this.logFollowRefreshing
      ) {
        return;
      }
      this.logFollowRefreshing = true;
      try {
        await this.reloadSelectedLog();
        this.scrollLogOutputToBottom();
      } finally {
        this.logFollowRefreshing = false;
      }
    },
    scrollLogOutputToBottom() {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            const output = Array.isArray(this.$refs.logOutput)
              ? this.$refs.logOutput[0]
              : this.$refs.logOutput;
            if (output) {
              output.scrollTop = output.scrollHeight;
            }
          });
        });
      });
    },
    async copyVisibleLogs() {
      const text = this.visibleLogText;
      if (!text) {
        this.logNotice = "No visible log output to copy.";
        return;
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          this.copyTextWithFallback(text);
        }
        this.logNotice = "Visible log output copied.";
        this.error = "";
      } catch (error) {
        this.error = "Failed to copy visible log output";
      }
    },
    copyTextWithFallback(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "readonly");
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    },
    downloadVisibleLogs() {
      const text = this.visibleLogText;
      if (!text) {
        this.logNotice = "No visible log output to download.";
        return;
      }
      const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = this.visibleLogFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      this.logNotice = "Visible log output downloaded.";
    },
    visibleLogFilename() {
      const source = this.selectedLog || "central-error-log";
      const safeSource = source
        .replace(/[^a-zA-Z0-9._-]+/g, "-")
        .replace(/^-+|-+$/g, "");
      return `${safeSource || "ops-log"}.txt`;
    },
    async loadUserDetail(userId) {
      try {
        const response = await apiClient.get(`/admin/ops/users/${userId}`, {
          params: this.opsParams({ range: this.range })
        });
        this.selectedUserDetail = response.data || null;
        this.userDialog = true;
      } catch (error) {
        this.error = "Failed to load user operations detail";
      }
    },
    async assumeIdentity(user) {
      if (!user || !user.id || this.assumingIdentityUserId !== null) return;
      this.assumingIdentityUserId = user.id;
      this.error = "";
      try {
        await this.$store.dispatch("assumeIdentity", user.id);
        this.$router.push("/");
      } catch (error) {
        this.error =
          (error.response &&
            error.response.data &&
            error.response.data.detail) ||
          "Failed to assume identity";
      } finally {
        this.assumingIdentityUserId = null;
      }
    },
    async runAction(target, action) {
      if (!this.actions.enabled || this.actionRunning) return;
      const confirmed = window.confirm(
        `Run ${action} on ${target}? This executes an allowlisted npm script on the server.`
      );
      if (!confirmed) return;
      this.actionRunning = true;
      this.error = "";
      try {
        const response = await apiClient.post(
          `/admin/ops/actions/${target}/${action}`,
          { confirm: true },
          { params: this.opsParams() }
        );
        const result = response.data || {};
        if (Number(result.returncode || 0) !== 0) {
          this.error = result.stderr || "Ops action failed";
        }
        await this.refreshAll();
      } catch (error) {
        this.error =
          (error.response &&
            error.response.data &&
            error.response.data.detail) ||
          "Failed to run ops action";
      } finally {
        this.actionRunning = false;
      }
    },
    count(key) {
      return (this.overview.counts && this.overview.counts[key]) || 0;
    },
    statusColor(status) {
      if (status === "up") return "success";
      if (status === "degraded") return "warning";
      return "error";
    },
    formatDate(value) {
      if (!value) return "n/a";
      return new Date(value).toLocaleString();
    },
    formatBytes(value) {
      if (!value) return "0 B";
      if (value < 1024) return `${value} B`;
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${(value / 1024 / 1024).toFixed(1)} MB`;
    },
    extractLeadingLogTimestamp(line) {
      const value = String(line || "");
      const match = value.match(
        /^(\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:[.,]\d+)?(?:Z|[+-]\d{2}:\d{2})?)/
      );
      return match ? match[1] : "";
    },
    formatLogTimestamp(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      const pad = part => String(part).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds()
      )}`;
    },
    mergedLogTimestamp(entry) {
      const inlineTimestamp = this.extractLeadingLogTimestamp(entry.line);
      if (inlineTimestamp) {
        return inlineTimestamp.replace("T", " ").replace(/Z$/, "");
      }
      return this.formatLogTimestamp(entry.timestamp);
    },
    mergedLogMessage(entry) {
      const line = String((entry && entry.line) || "");
      const inlineTimestamp = this.extractLeadingLogTimestamp(line);
      if (!inlineTimestamp) {
        return line;
      }
      return line.slice(inlineTimestamp.length).trimStart();
    },
    moneyish(value) {
      return Number(value || 0).toFixed(2);
    },
    modalTrueCostLabel(item) {
      if (item && item.true_cost_known) {
        return `$${this.moneyish(item.true_cost_usd)}`;
      }
      return "Unknown";
    }
  }
};
</script>

<style scoped>
.ops-view {
  min-height: 100vh;
}

.ops-card {
  min-height: 116px;
  background: var(--card-bg, #15151d) !important;
  border: 1px solid var(--border-color, #23232f) !important;
  border-radius: 8px;
}

.ops-overview-card {
  min-height: 0;
}

.ops-card .v-card__title {
  color: var(--text-secondary, #a1a1ae);
  font-size: 0.95rem;
  padding-bottom: 4px;
}

.ops-card .metric {
  color: var(--text-heading, #ffffff);
}

.metric {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
}

.detail-cell {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ops-toolbar {
  min-height: 72px;
}

.ops-toolbar ::v-deep .v-toolbar__content {
  height: auto !important;
  min-height: 72px;
  flex-wrap: wrap;
  padding-bottom: 8px;
  padding-top: 8px;
}

.ops-toolbar-select {
  background: rgba(0, 0, 0, 0.22);
  border-radius: 6px;
}

.ops-toolbar-select ::v-deep .v-input__control,
.ops-toolbar-select ::v-deep .v-input__slot {
  min-height: 40px !important;
}

.ops-toolbar-select ::v-deep .v-label {
  background: transparent;
  color: rgba(255, 255, 255, 0.75) !important;
  line-height: 1;
  padding: 0 4px;
}

.ops-toolbar-select ::v-deep .v-label--active {
  transform: translateY(-10px) scale(0.75);
}

.ops-toolbar-select ::v-deep .v-select__selection,
.ops-toolbar-select ::v-deep .v-icon {
  color: #ffffff !important;
}

.ops-toolbar-select ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.35) !important;
}

.runtime-select,
.host-select {
  max-width: 220px;
}

.range-select {
  max-width: 160px;
}

.ops-table-search {
  max-width: 280px;
}

.ops-pagination-bar {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.ops-page-size {
  max-width: 110px;
}

.log-source-list {
  max-height: 520px;
  overflow-y: auto;
}

.log-source-list ::v-deep .v-list-item__title {
  white-space: normal;
  word-break: break-word;
}

.log-controls {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.log-source-dot {
  border-radius: 999px;
  display: inline-block;
  height: 9px;
  margin-right: 8px;
  width: 9px;
}

.log-output {
  background: #101820;
  border-radius: 8px;
  color: #d9f7ef;
  font-size: 0.8rem;
  max-height: 560px;
  overflow: auto;
  padding: 16px;
  white-space: pre-wrap;
}

.log-output-merged {
  font-family: "Roboto Mono", Consolas, "Courier New", monospace;
  white-space: normal;
}

.log-line-row {
  align-items: flex-start;
  column-gap: 10px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  margin-bottom: 4px;
  min-width: 0;
}

.log-line-prefix {
  align-items: center;
  display: inline-flex;
  gap: 10px;
  white-space: nowrap;
}

.log-service-tag {
  border-radius: 4px;
  color: #071015;
  flex: 0 0 auto;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  margin-top: 1px;
  min-width: 76px;
  padding: 1px 6px;
  text-align: center;
}

.log-line-timestamp {
  color: #8fb3c8;
  font-variant-numeric: tabular-nums;
}

.log-line-text {
  color: #d9f7ef;
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  word-break: break-word;
}

.log-line-row:last-child {
  margin-bottom: 0;
}

.log-source-main_api,
.log-source-main_api.log-service-tag,
.log-source-main_api.log-source-dot {
  background: #64b5f6;
}

.log-source-backtest_execution_service,
.log-source-backtest_execution_service.log-service-tag,
.log-source-backtest_execution_service.log-source-dot {
  background: #81c784;
}

.log-source-deployment_infrastructure_service,
.log-source-deployment_infrastructure_service.log-service-tag,
.log-source-deployment_infrastructure_service.log-source-dot {
  background: #90a4ae;
}

.log-source-lean_aws_service,
.log-source-lean_aws_service.log-service-tag,
.log-source-lean_aws_service.log-source-dot {
  background: #a5d6a7;
}

.log-source-market_data_service,
.log-source-market_data_service.log-service-tag,
.log-source-market_data_service.log-source-dot {
  background: #ffca28;
}

.log-source-subscription_billing_service,
.log-source-subscription_billing_service.log-service-tag,
.log-source-subscription_billing_service.log-source-dot {
  background: #ef9a9a;
}

.log-source-user_service,
.log-source-user_service.log-service-tag,
.log-source-user_service.log-source-dot {
  background: #80deea;
}

.log-source-strategy_library_service,
.log-source-strategy_library_service.log-service-tag,
.log-source-strategy_library_service.log-source-dot {
  background: #ffb74d;
}

.log-source-copilotclone,
.log-source-copilotclone.log-service-tag,
.log-source-copilotclone.log-source-dot {
  background: #b39ddb;
}

.log-source-merged,
.log-source-merged.log-service-tag,
.log-source-merged.log-source-dot {
  background: #cfd8dc;
}

.log-source-unknown,
.log-source-unknown.log-service-tag,
.log-source-unknown.log-source-dot {
  background: #b0bec5;
}

.clickable-row {
  cursor: pointer;
}

.ops-user-detail-section {
  width: 100%;
}

.ops-user-detail-section__body {
  max-height: 260px;
  overflow: auto;
}
</style>
